import React, { useState, useEffect } from "react";
import {
  SOCIAL_RECOVERY_MODULE_ADDRESS,
  SOCIAL_RECOVERY_MODULE_ABI,
} from "../utils/Constants";
import { readContract, http, createConfig } from "@wagmi/core";
import { baseSepolia } from "@wagmi/core/chains";
import { metaMask } from "@wagmi/connectors";
import { useAccount, useChainId, useWriteContract } from "wagmi";
import { ethers } from "ethers";
import ConnectWalletButton from "./ConnectWalletButton";
import {
  CONTRACT_CONFIG,
  isSupportedChain,
  getContractAddress,
  getChainName,
  SUPPORTED_CHAINS,
} from "../utils/chainConfig";

function RecoveryExecutionForm({}) {
  const [walletAddress, setWalletAddress] = useState("");
  const [newOwner, setNewOwner] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [isInitialized, setIsInitialized] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [walletAddressError, setWalletAddressError] = useState("");
  const [newOwnerError, setNewOwnerError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const { address, isConnected } = useAccount();
  const chainId = useChainId();

  const { writeContract, isPending, isSuccess, isError, error } =
    useWriteContract();

  // Add new error handling for unsupported chains
  useEffect(() => {
    if (chainId && !isSupportedChain(chainId)) {
      setErrorMessage(
        `Please switch to a supported chain. Currently supported: ${SUPPORTED_CHAINS.map(
          (chain) => chain.name
        ).join(", ")}`
      );
    } else {
      setErrorMessage("");
    }
  }, [chainId]);

  useEffect(() => {
    if (isSuccess) {
      console.log("Recovery initiated successfully!");

      const fetchExecutionTime = async () => {
        try {
          setSuccessMessage(
            `Recovery transaction successful! You can finalize the recovery transaction at finalize tx in navbar`
          );
        } catch (error) {
          console.error("Error fetching recovery data:", error);
          setErrorMessage("Error fetching recovery data. Please try again.");
        }
      };

      fetchExecutionTime();
    }
    if (isError) {
      console.error("Error initiating recovery:", error);
      setErrorMessage(`Error initiating recovery: ${error.message}`);
    }
  }, [isSuccess, isError, error]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isFormValid()) {
      console.log("**@ handleSubmit called , form is valid");
      setErrorMessage("");
      setSuccessMessage("");
      await handleRecoveryTx(walletAddress, newOwner, signatures);
    }
  };

  const handleSignatureChange = (index, value) => {
    const newSignatures = [...signatures];
    newSignatures[index] = value;
    setSignatures(newSignatures);
  };

  const checkForRecovery = async () => {
    try {
      console.log("**@ checkForRecovery called ");
      if (!isSupportedChain(chainId)) {
        setErrorMessage(
          `Unsupported chain. Please switch to a supported chain.`
        );
        return;
      }

      console.log("**@ chain is supported ");

      const contractAddress = getContractAddress(chainId);
      console.log("**@ contractAddress is , ", contractAddress);

      const config = createConfig({
        chains: [SUPPORTED_CHAINS.find((chain) => chain.id === chainId)],
        transports: {
          [chainId]: http(),
        },
      });

      console.log("**@ config is , ", config);

      console.log("**@ walletAddress is , ", walletAddress);

      const isInitialized = await readContract(config, {
        address: contractAddress,
        abi: CONTRACT_CONFIG.abi,
        functionName: "isInitialized",
        args: [ethers.getAddress(walletAddress)],
      });

      console.log("**@ isInitialized is , ", isInitialized);

      if (!isInitialized) {
        setErrorMessage(
          "Social recovery module not installed for this smart contract wallet. Please install the social recovery module to try account recovery."
        );
        setIsInitialized(false);
      } else {
        const thresholdValue = await readContract(config, {
          address: contractAddress,
          abi: CONTRACT_CONFIG.abi,
          functionName: "thresholds",
          args: [ethers.getAddress(walletAddress)],
        });

        setThreshold(Number(thresholdValue));
        setSignatures(Array(Number(thresholdValue)).fill("")); // Initialize signature fields
        setIsInitialized(true);
        setErrorMessage(""); // Clear error message on success
      }
    } catch (error) {
      console.error("Error checking for recovery:", error);
      setErrorMessage("Error checking for recovery.");
      setIsInitialized(false); // Ensure the button reappears on error
    }
  };

  const handleWalletAddressChange = (e) => {
    const value = e.target.value;
    setWalletAddress(value);
    setIsInitialized(null); // Reset the initialization state when the address changes
    setErrorMessage(""); // Clear any existing error message

    if (!ethers.isAddress(value)) {
      setWalletAddressError("Invalid AXR wallet address.");
    } else {
      setWalletAddressError(null);
    }
  };

  const handleNewOwnerChange = (e) => {
    const value = e.target.value;
    setNewOwner(value);

    if (!ethers.isAddress(value)) {
      setNewOwnerError("Invalid new owner address.");
    } else {
      setNewOwnerError("");
    }
  };

  const isFormValid = () => {
    return (
      newOwner &&
      signatures.every((signature) => signature.trim() !== "") &&
      ethers.isAddress(newOwner) &&
      ethers.isAddress(walletAddress)
    );
  };

  const handleRecoveryTx = async (walletAddress, newOwner, signatures) => {
    console.log("**@ handleRecoveryTx called ");

    if (!isConnected) {
      console.log("**@ here 1");
      setErrorMessage("Please connect your wallet first.");
      return;
    }

    // if (chainId !== baseSepolia.id) {
    //   console.log("**@ here 2");
    //   setErrorMessage("Please switch to Base Sepolia network.");
    //   return;
    // }

    if (!isSupportedChain(chainId)) {
      setErrorMessage(
        `Please switch to a supported chain. Current chain (${getChainName(
          chainId
        )}) is not supported.`
      );
      return;
    }

    try {
      const contractAddress = getContractAddress(chainId);

      await writeContract({
        address: contractAddress,
        abi: CONTRACT_CONFIG.abi,
        functionName: "initiateRecovery",
        args: [walletAddress, newOwner, signatures],
      });
    } catch (error) {
      console.error("Error initiating recovery:", error);
      setErrorMessage("Error initiating recovery. Please try again.");
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <h2>Initiate Recovery Transaction</h2>
      <ConnectWalletButton />
      <div className="form-group">
        <label>AXR Wallet Address:</label>
        <input
          type="text"
          value={walletAddress}
          onChange={handleWalletAddressChange}
          required
        />
        {walletAddressError && (
          <p className="error-message">{walletAddressError}</p>
        )}
      </div>
      {isInitialized === null && (
        <button
          type="button"
          onClick={checkForRecovery}
          disabled={!!walletAddressError || !walletAddress}
        >
          Check for Recovery
        </button>
      )}
      {isInitialized === false && (
        <p className="error-message">{errorMessage}</p>
      )}
      {isInitialized === true && (
        <>
          <p>
            You need a threshold of {threshold} signatures to recover the
            wallet.
          </p>
          <div className="form-group">
            <label>New Owner Address:</label>
            <input
              type="text"
              value={newOwner}
              onChange={handleNewOwnerChange}
              required
            />
            {newOwnerError && <p className="error-message">{newOwnerError}</p>}
          </div>
          {signatures.map((signature, index) => (
            <div key={index} className="form-group">
              <label>Signature {index + 1}:</label>
              <input
                type="text"
                value={signature}
                onChange={(e) => handleSignatureChange(index, e.target.value)}
                required
              />
            </div>
          ))}
          <button type="submit" disabled={!isFormValid() || isPending}>
            {isPending ? "Initiating Recovery..." : "Initiate Recovery"}
          </button>
        </>
      )}
      {errorMessage && <p className="error-message">{errorMessage}</p>}
      {successMessage && <p className="success-message">{successMessage}</p>}
    </form>
  );
}

export default RecoveryExecutionForm;
